import { useCallback } from 'react';

import { PUBLISH_ERROR_TYPE } from 'app/components/commons/PublishModalError/PublishModalError';
import { usePublishRoom } from 'app/hooks/data/core/admin/rooms/usePublishRoom';
import { useUnpublishRoom } from 'app/hooks/data/core/admin/rooms/useUnpublishRoom';
import { usePublishOldRoom } from 'app/hooks/data/rooms/usePublishOldRoom';

import type { HotelRoom } from '../_types';

type Props = {
  hotelId: number;
  refetchRooms: () => Promise<unknown>;
  openPublishErrorModal: (error: unknown, type: PUBLISH_ERROR_TYPE) => void;
  isLegacy: boolean;
};

export const useRoomActions = ({
  hotelId,
  refetchRooms,
  openPublishErrorModal,
  isLegacy,
}: Props) => {
  const { mutateAsync: publishLegacyRoom } = usePublishOldRoom();
  const { mutateAsync: publishRoomMutation } = usePublishRoom();
  const { mutateAsync: unpublishRoomMutation } = useUnpublishRoom();

  const publishRoom = useCallback(
    async (room: HotelRoom) => {
      try {
        if (isLegacy === true) {
          await publishLegacyRoom(room.id.toString());
        } else {
          await publishRoomMutation({ roomId: room.id.toString(), hotelId });
        }

        await refetchRooms();
      } catch (error) {
        openPublishErrorModal(error, PUBLISH_ERROR_TYPE.ROOM);
      }
    },
    [
      hotelId,
      isLegacy,
      openPublishErrorModal,
      publishLegacyRoom,
      publishRoomMutation,
      refetchRooms,
    ]
  );

  const unpublishRoom = useCallback(
    async (room: HotelRoom) => {
      try {
        if (isLegacy) {
          await publishLegacyRoom(room.id.toString());
        } else {
          await unpublishRoomMutation({ roomId: room.id.toString(), hotelId });
        }

        await refetchRooms();
      } catch (error) {
        openPublishErrorModal(error, PUBLISH_ERROR_TYPE.ROOM);
      }
    },
    [
      hotelId,
      isLegacy,
      openPublishErrorModal,
      publishLegacyRoom,
      refetchRooms,
      unpublishRoomMutation,
    ]
  );

  return { publishRoom, unpublishRoom };
};
